import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { OperationResult } from '@/lib/operation-result';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { tokenRefreshService } from '@/lib/auth/token-refresh';
import { store } from '@/store/store';

/**
 * Custom error class that carries OperationResult data so the UI
 * can display meaningful error messages.
 */
export class ApiError extends Error {
  statusCode: number;
  errors: string[];
  isOffline: boolean;
  /** The client gave up waiting. A write may still have committed server-side. */
  isTimeout: boolean;
  /** Machine-readable failure code from the envelope, when the backend sent one. */
  errorCode?: string;
  metadata?: Record<string, unknown>;

  constructor(
    message: string,
    statusCode: number = 500,
    errors: string[] = [],
    isOffline = false,
    isTimeout = false,
    errorCode?: string,
    metadata?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errors = errors;
    this.isOffline = isOffline;
    this.isTimeout = isTimeout;
    this.errorCode = errorCode;
    this.metadata = metadata;
  }
}

export const isTimeoutError = (error: unknown): boolean =>
  error instanceof ApiError && error.isTimeout;

/**
 * The most specific line an unknown thrown value carries, for a toast.
 *
 * `ApiError.errors` is the backend's `OperationResult.errors` verbatim — on a
 * bulk endpoint that is one line per offending record, and the first is the
 * one worth leading with. `message` is the fallback because a failure envelope
 * can carry a message and an empty `errors`.
 *
 * Callers that can show more than one line (a dispatch rejecting five units,
 * say) should render `ApiError.errors` themselves rather than use this.
 */
export function apiErrorMessage(error: unknown, fallback = ''): string {
  if (error instanceof ApiError) {
    const [first] = error.errors;
    return first?.trim() ? first : error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

/**
 * A synchronous collect holds the request while the customer approves a prompt
 * on a handset; the backend chain waits up to 120s for that. Pass this on the
 * calls that run one, or the shared 30s budget reports a gateway error for a
 * payment still being decided.
 */
export const COLLECT_REQUEST_TIMEOUT_MS = 160_000;

class API {
  private axiosInstance;

  constructor() {
    const API_URL_BASE = import.meta.env?.VITE_API_URL_BASE ?? '/api';

    this.axiosInstance = axios.create({
      baseURL: API_URL_BASE,
      withCredentials: true,
      timeout: 30_000, // 30 seconds — backend may be slow on cold start
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const skipAuth =
          config.url?.includes('/auth/login') ||
          config.url?.includes('/auth/refresh');

        if (!skipAuth) {
          let token = await tokenRefreshService.getValidToken();
          // Fallback: read directly from localStorage if token-refresh service returns null
          if (!token) {
            token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
          }
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        const state = store.getState();
        const tenantId =
          state.tenant.selectedTenantId ?? state.auth.user?.tenantId ?? null;
        if (tenantId) {
          config.headers['X-Tenant-Id'] = tenantId;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;

        const statusCode = error.response?.status;
        if (statusCode === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (
            originalRequest.url?.includes('/auth/login') ||
            originalRequest.url?.includes('/auth/refresh')
          ) {
            return Promise.reject(error);
          }

          if (isRefreshing) {
            return new Promise((resolve) => {
              addRefreshSubscriber((token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.axiosInstance(originalRequest));
              });
            });
          }

          isRefreshing = true;

          try {
            const result = await tokenRefreshService.refresh();

            if (result.ok) {
              isRefreshing = false;
              onRefreshed(result.accessToken);
              originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
              return this.axiosInstance(originalRequest);
            }

            isRefreshing = false;
            // Drop the queue rather than leaving it holding promises that can
            // never settle — those requests would hang for the life of the page.
            refreshSubscribers = [];
            // Only a rejected session goes back to the login screen. A network
            // or cold-start failure preserves the session, so the operator
            // stays where they are and this one request simply fails.
            if (result.reason !== 'network') {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          } catch (refreshError) {
            isRefreshing = false;
            refreshSubscribers = [];
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async request<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.request<T>(config);
  }

  // Convenience methods that match axios API
  async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }
}

// Create and export singleton instance
export const api = new API();

/**
 * Handle API responses with OperationResult format.
 *
 * IMPORTANT: This function now THROWS on failure so that TanStack Query
 * properly detects errors (enables isError state and retries).
 * On success it returns the OperationResult as before.
 */
export const handleApiResponse = async <T>(
  apiCall: () => Promise<AxiosResponse<OperationResult<T>>>
): Promise<OperationResult<T>> => {
  try {
    const response = await apiCall();
    const data = response.data;

    // If the backend returned a valid OperationResult but it failed,
    // throw so TanStack Query treats this as an error.
    if (
      data &&
      typeof data === 'object' &&
      'succeeded' in data &&
      !data.succeeded
    ) {
      throw new ApiError(
        data.errors?.[0] || data.message || 'Request failed',
        data.statusCode ?? response.status,
        data.errors ?? [],
        false,
        false,
        data.errorCode,
        data.metadata
      );
    }

    return data;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }

    if (axios.isAxiosError(error)) {
      const data = error.response?.data;
      const isOffline =
        !error.response ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK';
      const isTimeout =
        error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';

      // If the backend already returned an OperationResult, extract info
      if (data && typeof data === 'object' && 'succeeded' in data) {
        throw new ApiError(
          data.errors?.[0] || data.message || error.message,
          data.statusCode ?? error.response?.status ?? 500,
          data.errors ?? [],
          isOffline,
          isTimeout,
          data.errorCode,
          data.metadata
        );
      }

      const message =
        data?.errors?.[0] ||
        data?.message ||
        extractFastApiDetail(data?.detail) ||
        error.message;
      // FastAPI's validation `detail[].loc` names the offending fields.
      throw new ApiError(
        message,
        error.response?.status ?? 503,
        [message],
        isOffline,
        isTimeout,
        undefined,
        Array.isArray(data?.detail) ? { detail: data.detail } : undefined
      );
    }

    throw new ApiError('An unexpected error occurred', 500, [
      'An unexpected error occurred',
    ]);
  }
};

/**
 * FastAPI 422 validation responses use `{ detail: [{ msg, loc, type, ... }] }`.
 * Default `String(detail)` would render `[object Object]`, so pull the first
 * useful message instead.
 */
function extractFastApiDetail(detail: unknown): string | null {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (!first) return null;
    if (typeof first === 'string') return first;
    if (typeof first === 'object' && first !== null) {
      const msg = (first as { msg?: unknown }).msg;
      if (typeof msg === 'string') return msg;
    }
  }
  if (typeof detail === 'object') {
    const msg = (detail as { msg?: unknown }).msg;
    if (typeof msg === 'string') return msg;
  }
  return null;
}

/**
 * Non-throwing version for cases where we want graceful degradation
 * (e.g. dashboard fallback data, settings).
 */
export const handleApiResponseSafe = async <T>(
  apiCall: () => Promise<AxiosResponse<OperationResult<T>>>
): Promise<OperationResult<T>> => {
  try {
    const response = await apiCall();
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data;
      if (data && typeof data === 'object' && 'succeeded' in data) {
        return data as OperationResult<T>;
      }
      const message =
        data?.errors?.[0] || data?.message || data?.detail || error.message;
      return {
        succeeded: false,
        errors: [message],
        hasErrors: true,
        statusCode: error.response?.status ?? 500,
      };
    }
    return {
      succeeded: false,
      errors: ['An unexpected error occurred'],
      hasErrors: true,
      statusCode: 500,
    };
  }
};

export default api;
